<template>
  <SettingsCard
    :title="$t('settings.features.return-call.title')"
    :subtitle="$t('settings.features.return-call.subtitle')"
    icon="mdi-phone"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <div class="py-4 px-4">
      <!-- Previous cols remain the same -->
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div>
            <div class="text-grey">
              {{ $t('settings.features.return-call.body-texts.0') }}
            </div>
            <div class="text-grey mt-4">
              {{ $t('settings.features.return-call.body-texts.1') }}
            </div>
          </div>
          <ChangeSettingsAlert :alert-text="$t('settings.features.return-call.body-texts.2')" />
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="px-0 px-md-2"
        >
          <v-row>
            <v-col
              cols="12"
              md="10"
              sm="8"
            >
              <v-btn
                class="w-full"
                @click="showCreateReturnCallDialog"
              >
                <v-icon :alt="$t('buttons.return-call.cta')"> mdi-plus </v-icon>
                {{ $t('buttons.return-call.cta') }}
              </v-btn>

              <v-list density="compact">
                <draggable
                  v-model="returnCallTopics"
                  handle=".js-draggable"
                  @start="drag = true"
                  @end="onDragEnd"
                >
                  <template
                    v-for="item in returnCallTopics"
                    :key="item.slug"
                  >
                    <v-list-item
                      v-if="!item.isHidden"
                      :ripple="false"
                      class="white return-call-item"
                    >
                      <div class="d-flex w-100">
                        <div class="d-flex align-center flex-grow-1">
                          <v-icon class="js-draggable reason-list__draggable mr-4">
                            mdi-drag-horizontal-variant
                          </v-icon>
                          <span>{{ item.label }}</span>
                        </div>

                        <div class="d-flex align-center">
                          <v-switch
                            :model-value="item.isActive"
                            color="primary"
                            class="ma-0 mr-4"
                            density="compact"
                            hide-details
                            @update:model-value="(value) => toggleActive(item, value)"
                          />

                          <v-tooltip
                            v-if="item.isCustom"
                            location="bottom"
                          >
                            <template v-slot:activator="{ props }">
                              <v-btn
                                color="error"
                                icon
                                variant="text"
                                v-bind="props"
                                @click.stop="() => hideTopic(item)"
                              >
                                <v-icon :alt="$t('labels.remove')"> mdi-trash-can-outline </v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t('labels.remove') }}</span>
                          </v-tooltip>
                        </div>
                      </div>
                    </v-list-item>
                  </template>
                </draggable>
              </v-list>

              <v-btn
                class="primary mt-4 w-full"
                :disabled="isLoading || !hasChanges"
                :loading="isLoading"
                @click="saveChanges"
              >
                {{ $t('buttons.return-call.save') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
    <ReturnCallDialog
      :show="showReturnCallDialog"
      :loading="isLoading"
      @addReason="addReturnCallReason"
      @close="showReturnCallDialog = false"
    />
  </SettingsCard>
</template>

<script>
import { VueDraggableNext } from 'vue-draggable-next'

import ChangeSettingsAlert from '@/components/ChangeSettingsAlert.vue'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import bus, { eventNames } from '@/lib/eventBus'
import featureNames from '@/lib/featureNames'
import featureMixin from '@/mixins/feature'

import ReturnCallDialog from './ReturnCallDialog.vue'

export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'return-call' })
    return {
      isOpen,
      setOpen,
    }
  },
  components: {
    SettingsCard,
    ChangeSettingsAlert,
    ReturnCallDialog,
    draggable: VueDraggableNext,
  },
  mixins: [featureMixin],
  data: function () {
    return {
      returnCallTopics: [],
      showReturnCallDialog: false,
      hasChanges: false,
      isLoading: false,
      drag: false,
    }
  },
  created() {
    this.returnCallTopics = this.getTopics()
    bus.$on(eventNames.CHANGE_FEATURE_CONFIG, (event) => {
      if (event.slug !== featureNames.RETURNCALL_TOPICS) return
      if (event.error)
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.features.error'),
        })
    })
  },
  methods: {
    getTopics() {
      return [...this.$features._featuresBySlug[featureNames.RETURNCALL_TOPICS].config.topics]
    },
    toggleActive(item, value) {
      this.returnCallTopics = this.returnCallTopics.map((topic) =>
        topic.slug === item.slug ? { ...topic, isActive: value } : topic
      )
      this.updateTopic(item)
    },
    showCreateReturnCallDialog() {
      this.$tracking.event('Settings', 'Clicked', 'Add callback reason')
      this.showReturnCallDialog = true
    },
    async addReturnCallReason(reason) {
      const slug = reason.trim().replaceAll(' ', '_').toUpperCase()
      const existingItem = this.returnCallTopics.find((item) => item.slug === slug)

      if (existingItem && existingItem.isActive && !existingItem.isHidden) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.return-call.error'),
        })
        return
      }

      if (existingItem) {
        this.returnCallTopics = this.returnCallTopics.map((item) =>
          item.slug === slug ? { ...item, isActive: true, isHidden: false } : item
        )
      } else {
        this.returnCallTopics.unshift({
          slug,
          label: reason,
          isActive: true,
          isCustom: true,
        })
      }

      try {
        this.isLoading = true
        await this.$features.setCompanyFeature({
          companyId: this.$auth.user.companyId,
          slug: featureNames.RETURNCALL_TOPICS,
          config: {
            topics: this.returnCallTopics,
          },
        })
        this.$tracking.event('Settings', 'Clicked', 'Add callback reason')

        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.return-call.success'),
        })

        // Close dialog after successful save
        this.showReturnCallDialog = false
      } catch (e) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.features.error'),
        })
      } finally {
        this.isLoading = false
      }
    },
    updateTopic() {
      this.$tracking.event('Settings', 'Clicked', 'Update Topic')
      this.hasChanges = true
    },
    hideTopic(topic) {
      this.hasChanges = true
      this.returnCallTopics = this.returnCallTopics.map((item) =>
        item.slug === topic.slug ? { ...item, isActive: false, isHidden: true } : item
      )
    },
    onDragEnd() {
      this.drag = false
      this.hasChanges = true
    },
    async saveChanges() {
      try {
        this.isLoading = true
        await this.$features.setCompanyFeature({
          companyId: this.$auth.user.companyId,
          slug: featureNames.RETURNCALL_TOPICS,
          config: {
            topics: this.returnCallTopics,
          },
        })
        this.hasChanges = false
        this.$tracking.event('Settings', 'Clicked', 'Return call Save Changes')
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.features.success'),
        })
      } catch (e) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.features.error'),
        })
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style>
.reason-list__draggable {
  cursor: move;
}

.v-list-item {
  margin-bottom: 2px;
}

.return-call-item :deep(.v-switch) {
  margin-top: 0 !important;
}

.return-call-item {
  padding-right: 8px !important;
}

.return-call-item:nth-child(even) {
  background-color: rgb(var(--v-theme-grey-lighten-4)) !important;
}
</style>
